/**
 * L'historique des versions de Poissonpédia, **sans DOM**.
 *
 * C'est la guerre d'édition, et elle se lit de haut en bas : un patrouilleur
 * annule « un poisson Steve » pour la septième fois, Vigz rétablit les bras
 * qu'Anonyme_88 avait retirés au motif qu'un poisson n'a pas de bras, un
 * ichtyologue retraité mesure le diamètre de l'œil.
 *
 * Sortir ça d'un composant sert à deux choses : les dates sont relatives à
 * aujourd'hui, donc impossibles à figer dans une capture, et publier une
 * modification doit **ajouter une version en tête** sans jamais casser l'ordre
 * ni le drapeau « actuel », qui n'appartient qu'à la première.
 */

import { S, t, type Leaf, type Locale } from '../../i18n/strings';

/** Une ligne de l'historique. */
export interface Revision {
  readonly id: number;
  /** Nombre de jours en arrière, converti en date à l'affichage. */
  readonly daysAgo: number;
  readonly user: Leaf;
  readonly size: number;
  readonly comment: Leaf;
}

/** Les treize versions d'origine, de la plus récente à la création de la page. */
const SEED: readonly { daysAgo: number; user: Leaf; size: number; comment: Leaf }[] = [
  { daysAgo: 0, user: S.wiki.users.patroller, size: 4612, comment: S.wiki.revisions.r1 },
  { daysAgo: 0, user: { fr: 'Vigz', en: 'Vigz' }, size: 4604, comment: S.wiki.revisions.r2 },
  { daysAgo: 0, user: S.wiki.users.anon, size: 4498, comment: S.wiki.revisions.r3 },
  { daysAgo: 1, user: S.wiki.users.ichthyo, size: 4604, comment: S.wiki.revisions.r4 },
  { daysAgo: 1, user: S.wiki.users.bot, size: 4390, comment: S.wiki.revisions.r5 },
  { daysAgo: 2, user: S.wiki.users.patroller, size: 4388, comment: S.wiki.revisions.r6 },
  { daysAgo: 2, user: S.wiki.users.anon, size: 4380, comment: S.wiki.revisions.r7 },
  { daysAgo: 3, user: S.wiki.users.ichthyo, size: 4402, comment: S.wiki.revisions.r8 },
  { daysAgo: 4, user: { fr: 'Vigz', en: 'Vigz' }, size: 4211, comment: S.wiki.revisions.r9 },
  { daysAgo: 5, user: S.wiki.users.anon, size: 4118, comment: S.wiki.revisions.r10 },
  { daysAgo: 5, user: S.wiki.users.brave, size: 4118, comment: S.wiki.revisions.r11 },
  { daysAgo: 8, user: { fr: 'Vigz', en: 'Vigz' }, size: 4102, comment: S.wiki.revisions.r12 },
  { daysAgo: 13, user: S.wiki.users.creator, size: 1284, comment: S.wiki.revisions.r13 },
];

/** L'historique au chargement. */
export function seedRevisions(): readonly Revision[] {
  return SEED.map((revision, index) => ({ id: SEED.length - index, ...revision }));
}

/**
 * Ajoute la modification du visiteur en tête.
 *
 * Taille tirée autour de 4 200 octets : une version sans taille n'aurait l'air
 * de rien, et une taille fixe se remarquerait dès la deuxième publication.
 */
export function publish(
  history: readonly Revision[],
  summary: string,
  random: () => number = Math.random,
): readonly Revision[] {
  const trimmed = summary.trim();
  const next: Revision = {
    id: (history[0]?.id ?? 0) + 1,
    daysAgo: 0,
    user: S.wiki.history.you,
    size: 4200 + Math.floor(random() * 120) - 60,
    comment: trimmed === '' ? S.wiki.history.no_summary : { fr: trimmed, en: trimmed },
  };
  return [next, ...history];
}

/** La date affichée d'une version, en format encyclopédique. */
export function revisionDate(daysAgo: number, lang: Locale, now: Date = new Date()): string {
  const when = new Date(now.getTime() - daysAgo * 86_400_000);
  const date = when.toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const time = when.toLocaleTimeString(lang === 'fr' ? 'fr-FR' : 'en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${date} à ${time}`.replace(' à ', lang === 'fr' ? ' à ' : ', ');
}

/** « 4 612 octets » — le compte se lit toujours groupé, comme sur une vraie page. */
export function formatSize(size: number, lang: Locale): string {
  const n = size.toLocaleString(lang === 'fr' ? 'fr-FR' : 'en-GB');
  return `${n} ${t(S.wiki.history.bytes, lang)}`;
}
